// /contacts — lista paginada con búsqueda + botón "+ Nuevo contacto".
// Sprint S0.3 P0.

import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Plus,
  Mail,
  Phone,
  Building,
  Users,
  X as XIcon,
} from 'lucide-react';
import { Avatar } from '~/components/Avatar/Avatar';
import { useContactsList, useContactsLeadCount } from '~/lib/contacts-queries';
import { useActiveTenantId } from '~/lib/tenantContext';
import type { Contact } from '~/lib/types';
import { personDisplayName } from '~/lib/display-name';
import { prettyPhone } from '~/lib/phone';
import { NewContactModal } from '~/components/NewContactModal/NewContactModal';
import styles from './_authed.contacts.module.css';

export const Route = createFileRoute('/_authed/contacts')({
  component: ContactsListPage,
});

const PER_PAGE = 25;

function contactDisplayName(c: Contact): string {
  return personDisplayName(c) || (c.phone ? prettyPhone(c.phone) : null) || 'Sin nombre';
}

function formatRelativeDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

function ContactsListPage() {
  const tenantId = useActiveTenantId();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);

  // Reset página al cambiar búsqueda
  useEffect(() => {
    setPage(1);
  }, [search]);

  const { data, isLoading, isError } = useContactsList({
    search,
    page,
    perPage: PER_PAGE,
  });
  const items = data?.items ?? [];
  const totalItems = data?.totalItems ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const contactIds = useMemo(() => items.map((c) => c.id), [items]);
  const { data: leadCounts } = useContactsLeadCount(contactIds);

  if (!tenantId) {
    return (
      <div className={styles.page}>
        <div className="empty-state">Selecciona un cliente en el switcher del topbar.</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="h2">Contactos</h1>
        </div>
        <div className="page-header__actions">
          <div className={styles.totalBadge}>
            <strong>{totalItems}</strong>
            <span className={styles.muted}>{totalItems === 1 ? 'contacto' : 'contactos'}</span>
          </div>
          <button type="button" className="btn btn--primary" onClick={() => setShowNew(true)}>
            <Plus size={14} aria-hidden /> Nuevo contacto
          </button>
        </div>
      </header>

      {showNew && <NewContactModal onClose={() => setShowNew(false)} />}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <Search size={14} aria-hidden />
          <input
            type="text"
            className={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar contactos…"
          />
          {search && (
            <button
              type="button"
              className={styles.clearBtn}
              onClick={() => setSearch('')}
              aria-label="Limpiar búsqueda"
            >
              <XIcon size={12} aria-hidden />
            </button>
          )}
        </div>
      </div>

      {/* Tabla */}
      {isLoading ? (
        <div className="empty-state">Cargando…</div>
      ) : isError ? (
        <div className="empty-state">
          No pudimos cargar los contactos. Recarga o intenta más tarde.
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          {search ? (
            <p>
              No hay contactos que coincidan con <strong>«{search}»</strong>.
            </p>
          ) : (
            <>
              <Users className="empty-state__icon" size={28} aria-hidden />
              <p>
                Aún no tienes contactos. Van a aparecer cuando entren oportunidades, o puedes crear
                uno con el botón de arriba.
              </p>
            </>
          )}
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Contacto</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>Empresa</th>
                <th className={styles.colNum}>Oportunidades</th>
                <th>Última actividad</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => {
                const name = contactDisplayName(c);
                const leadCount = leadCounts?.[c.id] ?? '—';
                return (
                  <tr key={c.id} className={styles.tr}>
                    <td className={styles.cellName}>
                      <Link
                        to="/contacts/$contactId"
                        params={{ contactId: c.id }}
                        className={styles.cellLink}
                      >
                        <Avatar name={name} size="sm" />
                        <span className={styles.cellNameText}>{name}</span>
                      </Link>
                    </td>
                    <td className={styles.cellMuted}>
                      {c.email ? (
                        <span className={styles.iconText}>
                          <Mail size={12} aria-hidden /> {c.email}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.cellMuted}>
                      {c.phone ? (
                        <span className={styles.iconText}>
                          <Phone size={12} aria-hidden /> {prettyPhone(c.phone)}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.cellMuted}>
                      {c.company ? (
                        <span className={styles.iconText}>
                          <Building size={12} aria-hidden /> {c.company}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className={styles.colNum}>{leadCount}</td>
                    <td className={styles.cellMuted}>{formatRelativeDate(c.updated)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            <ChevronLeft size={14} aria-hidden /> Anterior
          </button>
          <span className={styles.pageLabel}>
            Página {page} de {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Siguiente <ChevronRight size={14} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
